// SharedGroupView.js - View shared group content via token
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSharingGroupByToken, canAccessSharingGroup, calculateSharingGroupScope } from '../services/sharingGroups';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import RunsList from './RunsList';
import RacersList from './RacersList';
import GroupsList from './GroupsList';

const SharedGroupView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Navigate to runs list with token when group is loaded
  useEffect(() => {
    if (groupData && token && !loading) {
      // Navigate to runs list with shareGroupToken parameter
      navigate(`/runs?shareGroupToken=${token}`, { replace: true });
    }
  }, [groupData, token, loading, navigate]);

  useEffect(() => {
    const loadGroup = async () => {
      if (!token) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        // Ensure we have an authenticated user (Firestore queries require auth)
        let currentUser = user;
        
        if (!currentUser) {
          try {
            // Sign in anonymously for public access
            await signInAnonymously(auth);
            
            // Wait for auth state to be ready
            currentUser = await new Promise((resolve, reject) => {
              const unsubscribe = onAuthStateChanged(auth, (authUser) => {
                if (authUser) {
                  unsubscribe();
                  // Small delay to ensure Firestore has the auth token
                  setTimeout(() => resolve(authUser), 300);
                }
              });
              
              setTimeout(() => {
                unsubscribe();
                reject(new Error('Auth timeout'));
              }, 5000);
            });
          } catch (authError) {
            console.error('Auth error:', authError);
            // If anonymous auth is disabled, show helpful error
            if (authError.code === 'auth/admin-restricted-operation') {
              setError('Anonymous authentication is not enabled in Firebase. To enable public sharing, please enable Anonymous authentication in Firebase Console: Authentication > Sign-in method > Anonymous. Alternatively, please sign in to access this content.');
            } else {
              setError('Failed to authenticate. Please sign in to access this content.');
            }
            setLoading(false);
            return;
          }
        }

        // Now query for the sharing group (requires auth)
        const data = await getSharingGroupByToken(token);
        
        if (!data) {
          setError('Share link not found or invalid');
          setLoading(false);
          return;
        }

        // Check if user can access
        const canAccess = await canAccessSharingGroup(data.id, currentUser.uid, currentUser.isAnonymous || false);
        if (!canAccess && !data.isPublic) {
          setError('You do not have access to this sharing group.');
          setLoading(false);
          return;
        }

        // Check permission for write access
        if (data.permission === 'write' && currentUser.isAnonymous) {
          setError('Write access requires a signed-in account. Please sign in to continue.');
          setLoading(false);
          return;
        }

        setGroupData(data);
      } catch (err) {
        console.error('Error loading sharing group:', err);
        setError(err.message || 'Failed to load share link. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadGroup();
  }, [token, user]);

  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        padding: '40px 20px', 
        textAlign: 'center' 
      }}>
        <p>Loading shared content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        width: '100%', 
        padding: '40px 20px', 
        textAlign: 'center' 
      }}>
        <p style={{ color: '#d32f2f', marginBottom: '20px' }}>{error}</p>
        {error.includes('sign in') && (
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#13A1A8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'medium'
            }}
          >
            Sign In
          </button>
        )}
      </div>
    );
  }

  if (!groupData) {
    return null;
  }

  // Show all shared content - runs, racers, and groups
  // The list components will filter based on the token in URL
  return (
    <div>
      <div style={{ padding: '20px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <h2 style={{ margin: 0, marginBottom: '10px' }}>{groupData.name}</h2>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9em' }}>
          Permission: {groupData.permission === 'read' ? 'Read-only' : 'Read & Write'}
        </p>
      </div>
      <RunsList />
    </div>
  );
};

export default SharedGroupView;
